/** Map doctor API row to profile form state. */
export const mapDoctorToProfileForm = (doc) => {
  if (!doc) return {};

  const asStr = (v) => (v != null && v !== "" ? String(v) : "");

  const phoneNumbers = Array.isArray(doc.phone_numbers)
    ? doc.phone_numbers.filter(Boolean)
    : [doc.phone_number_1, doc.phone_number_2].filter(Boolean);

  return {
    doctor_id: doc.doctor_id,
    doctor_name: doc.doctor_name || "",
    payout_percentage: doc.payout_percentage ?? "",
    engagement_letter_path: doc.engagement_letter_path || "",
    establishment_ids: doc.establishment_ids
      ? String(doc.establishment_ids)
      : asStr(doc.establishment_id),
    establishment_id: asStr(
      String(doc.establishment_ids || "")
        .split(",")
        .map((s) => s.trim())
        .find(Boolean) || doc.establishment_id
    ),
    establishment_name: doc.establishment_name || "",
    user_type_id: asStr(doc.user_type_id),
    user_type: doc.user_type || "",
    country_id: doc.country_id ?? "",
    location_id: doc.location_id ?? "",
    country_name: doc.country_name || "",
    location_name: doc.location_name || "",
    state_name: doc.state_name || "",
    city_name: doc.city_name || "",
    medical_specialty_id: asStr(doc.medical_specialty_id),
    medical_specialty: doc.medical_specialty || "",
    procedure_ids: doc.procedure_ids || "",
    specialization_ids: doc.specialization_ids || "",
    department_id: asStr(doc.department_id),
    department_name: doc.department_name || "",
    designation_id: asStr(doc.designation_id),
    designation: doc.designation || "",
    degree_ids: doc.degree_ids || "",
    registration_year: asStr(doc.registration_year),
    accreditation_ids: doc.accreditation_ids || "",
    short_profile: doc.short_profile || "",
    long_profile: doc.long_profile || "",
    profile_pic_path: doc.profile_pic_path || "",
    profile_pdf_path: doc.profile_pdf_path || "",
    video_bio_url: doc.video_bio_url || "",
    articles_published: doc.articles_published ?? "",
    surgeries_count: doc.surgeries_count ?? "",
    country_code: doc.country_code || "",
    phone_numbers: phoneNumbers.length ? phoneNumbers : [""],
    phone_number_1: doc.phone_number_1 || phoneNumbers[0] || "",
    phone_number_2: doc.phone_number_2 || phoneNumbers[1] || "",
    address: doc.address || "",
    address_line_2: doc.address_line_2 || "",
    state_id: doc.state_id ?? "",
    city_id: doc.city_id ?? "",
    longitude: doc.longitude ?? "",
    latitude: doc.latitude ?? "",
    email_address: doc.email_address || "",
    website_address: doc.website_address || "",
    facebook_page: doc.facebook_page || "",
    youtube_channel: doc.youtube_channel || "",
    instagram: doc.instagram || "",
    linkedin: doc.linkedin || "",
    x_profile: doc.x_profile || "",
    telegram_channel: doc.telegram_channel || "",
  };
};

/** Merge saved doctor with in-progress form edits. */
export const mergeDoctorProfileFormData = (savedDoctor, formData = {}, { lockBasicFields = false } = {}) => {
  const base = mapDoctorToProfileForm(savedDoctor);
  if (!savedDoctor?.doctor_id) return { ...formData };
  const merged = {
    ...base,
    ...formData,
    doctor_id: savedDoctor.doctor_id,
  };
  if (lockBasicFields) {
    merged.doctor_name = savedDoctor.doctor_name || base.doctor_name;
    merged.establishment_id = savedDoctor.establishment_id ?? base.establishment_id;
    merged.user_type_id = savedDoctor.user_type_id ?? base.user_type_id;
    merged.country_id = savedDoctor.country_id ?? base.country_id;
    merged.location_id = savedDoctor.location_id ?? base.location_id;
  }
  return merged;
};

export const hasDoctorProfileData = (doc) => {
  if (!doc) return false;
  return Boolean(
    doc.medical_specialty_id
    || doc.short_profile
    || doc.long_profile
    || doc.department_id
    || doc.email_address
  );
};

export const resolveDegreeLabels = (degreeIds, degreesMaster = []) => {
  if (!degreeIds) return [];
  return degreeIds
    .toString()
    .split(",")
    .map((id) => degreesMaster.find((d) => String(d.medical_degree_id) === id.trim()))
    .filter(Boolean)
    .map((d) => d.medical_degree || d.degree_name || `Degree #${d.medical_degree_id}`);
};

/** Map doctor API row to DoctorManagementView display shape. */
export const mapDoctorApiToView = (doc, { degrees = [] } = {}) => {
  if (!doc) return null;

  const qualifications = resolveDegreeLabels(doc.degree_ids, degrees);
  const subtitleParts = [
    doc.establishment_name,
    [doc.city_name, doc.state_name].filter(Boolean).join(", "),
    qualifications.slice(0, 3).join(", "),
  ].filter(Boolean);

  const experienceYears = doc.registration_year
    ? `${Math.max(0, new Date().getFullYear() - Number(doc.registration_year))} yrs experience`
    : null;

  const addressParts = [doc.address, doc.city_name, doc.state_name, doc.country_name].filter(Boolean);
  const phones = Array.isArray(doc.phone_numbers)
    ? doc.phone_numbers.filter(Boolean)
    : [doc.phone_number_1, doc.phone_number_2].filter(Boolean);

  return {
    id: doc.doctor_id,
    prefix: [doc.designation, doc.department_name].filter(Boolean).join(" — ") || doc.user_type || "",
    name: doc.doctor_name || "Doctor",
    specialty: doc.medical_specialty || "",
    subtitle: subtitleParts.join(" · "),
    experience: experienceYears || "—",
    consultFee: "—",
    language: "—",
    about: doc.long_profile || doc.short_profile || "",
    qualifications,
    introductionVideo: doc.video_bio_url || "",
    profilePicPath: doc.profile_pic_path || "",
    publications: doc.articles_published || "",
    surgeriesCount: doc.surgeries_count ?? null,
    fees: { consultation: "—", followUp: "—" },
    registration: {
      council: doc.registration_year ? `Registered ${doc.registration_year}` : "—",
      number: doc.registration_certificate_path ? "Certificate on file" : "—",
    },
    contact: {
      phone: phones[0] || "—",
      email: doc.email_address || "—",
      address: addressParts.join(", ") || "—",
    },
    social: {
      website_address: doc.website_address || "",
      facebook_page: doc.facebook_page || "",
      x_profile: doc.x_profile || "",
      instagram: doc.instagram || "",
      linkedin: doc.linkedin || "",
      youtube_channel: doc.youtube_channel || "",
      telegram_channel: doc.telegram_channel || "",
    },
    profilePdfPath: doc.profile_pdf_path || "",
  };
};
